import Preact from 'preact'
import { Ludo, Clips } from '../../../../../types'
import * as styles from './index.css'
import { ClipsNav } from './ClipNav'
import { shallowEqual } from '../../../../../lib/shallow-equal'
import { saferPromise } from '../../../../../lib/promise-utils'
import { ViewCounter } from '../ViewCounter'
import { LikeButton } from '../LikeButton'
import { firebaseInstance } from '../../../../db/firebase'
import { waitUntil } from '../../../../../lib/wait-until'
import { FadeIn } from '../../../lib/FadeIn'
import { bem } from '@nrk/dh-utils'

const { h, Component } = Preact

/*
Notes: hvis det bare er et klipp begynner klippet på nytt, den slutter ikke på slutten av et spilleliste.

*/

interface IProps {
  clips: Clips
  clipIds: string[]
  onItemChanged?: (index: number) => void
  title?: string
  subTitle?: string
  showLikes: boolean
  showViews: boolean
}

interface IState {
  time: Array<{ currentTime; duration }>
  currentIndex: number
  controlsVisible: boolean
  playing: boolean
  muted: boolean
}

class Video extends Component<IProps, IState> {
  public autoplayTimeout: NodeJS.Timer = null
  public ludoInstance: any = null
  public ludoMount: HTMLElement = null
  public ludo: Ludo
  public state = {
    time: [],
    playing: false,
    muted: false,
    currentIndex: 0,
    controlsVisible: false
  }

  public fetchLudo = async () => {
    if (!this.ludo) {
      await waitUntil(() => !!window.requirejs, 500)
      await new Promise((resolve, reject) =>
        window.requirejs(
          ['https://static.nrk.no/ludo/4.4.44/ludo.js'],
          ludo => {
            if (ludo) {
              this.ludo = ludo
              resolve()
            } else {
              reject()
            }
          }
        )
      )
    }
  }

  public mountLudo = async (clipIds: string[]) => {
    try {
      if (!this.ludoMount) return
      if (!this.ludo) {
        await saferPromise(await this.fetchLudo, {
          attempts: 3,
          delay: [250, 2500]
        })
      }
      if (this.ludoInstance) {
        this.ludoInstance.removeAllListeners()
        this.ludoInstance = null
        this.ludoMount.innerHTML = ''
      }

      this.ludoInstance = this.ludo(this.ludoMount, clipIds, {
        loop: false,
        mute: false,
        autoplay: false,
        debug: false,
        videoPlayerAspectRatio: '16:9',
        autoadvance: true,
        features: {
          airplay: false,
          chromecast: false,
          help: false,
          navKeys: false
        },
        poster: {
          title: false,
          subtitle: false,
          duration: true
        },
        chromecast: {
          enabled: false
        },
        analytics: {
          ga: {
            trackingId: 'UA-3413696-1'
          }
        }
      })

      this.ludoInstance.on('item-changed', e => {
        const index = this.ludoInstance.index()

        this.setState({ currentIndex: index })
        if (this.props.onItemChanged) this.props.onItemChanged(index)
      })
      this.ludoInstance.on('play', e => {
        this.setState({ playing: true })
      })
      this.ludoInstance.on('pause', e => {
        this.setState({ playing: false })
      })
      this.ludoInstance.on('unmuted', e => {
        this.setState({ muted: false })
      })
      this.ludoInstance.on('muted', e => {
        this.setState({ muted: true })
      })

      this.ludoInstance.on('timeupdate', this.onTimeUpdate)
    } catch (err) {
      console.error('Tried to mount Ludo', err)
    }
  }

  public async componentDidMount() {
    this.mountLudo(this.props.clipIds)
  }

  public componentWillReceiveProps(nextProps) {
    const currentClipIds = this.props.clipIds
    const newClipIds = nextProps.clipIds
    const diff = diffClipIds(currentClipIds, newClipIds)
    if (diff) {
      this.mountLudo(nextProps.clipIds)
    }
  }

  public componentWillUnmount() {
    if (this.ludoInstance) this.ludoInstance.removeAllListeners()
    this.ludoInstance = null
    this.ludoMount = null
    this.ludo = null
  }

  public render() {
    const { title, subTitle, clips, showLikes, showViews } = this.props
    const { currentIndex } = this.state
    const currentClip = clips[currentIndex]

    return (
      <div class={styles.video}>
        <div class={styles.video__wrapper}>
          {title ? (
            <div class={styles['video__title-block']}>
              {subTitle && <div>{subTitle}</div>}
              {title && <h1 class={styles.video__title}>{title}</h1>}
            </div>
          ) : null}

          <div
            class={styles.video__element}
            ref={el => (this.ludoMount = el)}
          />
          <VideoDescription
            currentClip={currentClip}
            currentIndex={currentIndex}
            clipLength={clips.length}
          />
        </div>

        {clips.length > 1 && (
          <ClipsNav
            items={clips}
            onClick={this.onPlayAtIndex}
            currentIndex={currentIndex}
            showLikes={showLikes}
            showViews={showViews}
          />
        )}
      </div>
    )
  }

  private onPlayClick = () => {
    try {
      const btn = this.ludoMount.querySelector(
        'button.ludo-poster__accessible-play-button'
      )

      if (btn instanceof HTMLButtonElement) {
        btn.click()
      } else {
        this.ludoInstance.play()
      }
    } catch (err) {
      console.error('Error in play')
    }
  }

  private onPauseClick = () => {
    this.ludoInstance.pause()
  }

  private onToggleMuteClick = () => {
    const { isMuted, unmute, mute, set } = this.ludoInstance
    if (isMuted()) {
      unmute()
    } else {
      mute()
    }
  }

  private onNextClick = () => {
    this.ludoInstance.next()
  }

  private onPreviousClick = () => {
    const { playAt, index } = this.ludoInstance
    playAt(index() - 1)
  }

  private onPlayAtIndex = (index: number) => {
    const { playAt } = this.ludoInstance

    this.onPlayClick()
    playAt(index)
  }
  private onRestartClipClick = () => {
    this.ludoInstance.seekTo(0)
  }

  private onFullscreenToggleClick = () => {
    this.ludoInstance.fullscreen()
  }

  private onTimeUpdate = (time: number) => {
    incrementView(time, this.props.clipIds[this.state.currentIndex])
  }
}

const VideoDescription = ({ currentClip, currentIndex, clipLength }) => {
  const { title, id } = currentClip
  const playlist = clipLength > 1
  return (
    <FadeIn
      class={bem(styles['video__description-block'], { playlist })}
      key={id}
    >
      <div class={styles.video__meta}>
        {playlist && (
          <div class={styles['video__playlist-count']}>
            {currentIndex + 1} av {clipLength}
          </div>
        )}
        <ViewCounter clip={currentClip} />
        {/* <LikeButton clip={currentClip} /> */}
      </div>
      <div>
        <h2>{title}</h2>
      </div>
    </FadeIn>
  )
}

const MIN_TIME_UPDATE = 18

let viewCheck = {
  id: null,
  timeUpdateCount: 0,
  hasIncremented: false
}

const incrementView = async (time: number, id: string) => {
  if (!id) {
    return
  } else if (id !== viewCheck.id) {
    viewCheck = {
      id,
      timeUpdateCount: 0,
      hasIncremented: false
    }
  } else if (viewCheck.hasIncremented) {
    return
  } else if (viewCheck.timeUpdateCount >= MIN_TIME_UPDATE) {
    viewCheck.hasIncremented = true
    viewCheck.timeUpdateCount += 1
    await waitUntil(() => !!firebaseInstance, 1000)
    firebaseInstance.incrementViewCount(id)
  } else {
    viewCheck.timeUpdateCount += 1
  }
}

export { Video }

const diffClipIds = (clips1: string[], clips2: string[]) => {
  if (clips1.length !== clips2.length) return true

  const clipsInBoth = clips1.filter((clip, idx) => clip === clips2[idx])
  return clipsInBoth.length !== clips1.length
}
